import decodeHtml from '../../utilities/decodeHtml';
import MixcloudCloudcast from '../mixcloud/mixcloudCloudcast';
import mixcloudArtistFromTitle from './mixcloudArtistFromTitle';
import Source from '../source';
import Track from './track';
import TrackLength from './trackLength';

/**
 * Whether to guess the DJ out of the show title when nothing else names one.
 *
 * A module flag rather than a parameter because `from` is used as a bare
 * mapper in half a dozen places - `results.map(MixcloudTrack.from)`,
 * `createTrackSortValue(MixcloudTrack.from)` - and threading a setting
 * through all of them to reach one line is worse than this. Kept in step
 * with the setting by contextService.
 *
 * Delete this, `mixcloudArtistFromTitle.ts` and the setting together; see
 * that file for why the guessing exists at all.
 */
let artistFromTitleEnabled = false;

export function setMixcloudArtistFromTitle(enabled: boolean) {
  artistFromTitleEnabled = enabled;
}

/**
 * The credit line for a show.
 *
 * Mixcloud has no artist field: `hosts` exists in the API but was empty on
 * every show sampled, so in practice this is the uploader - which for an
 * archive or a radio account is not who played. With the setting on, a title
 * that plainly names the DJ is believed over the account.
 */
function artistOf(cloudcast: MixcloudCloudcast) {
  if (cloudcast.hosts && cloudcast.hosts.length) {
    return cloudcast.hosts.map((host) => host.name).join(', ');
  }

  if (artistFromTitleEnabled) {
    const guessed = mixcloudArtistFromTitle(cloudcast);

    if (guessed) {
      return guessed.artist;
    }
  }

  return cloudcast.user ? cloudcast.user.name : '';
}

/**
 * A Mixcloud show as the library stores it.
 *
 * `id` is the cloudcast key (`/user/slug/`), which is both what the API
 * addresses the show by and what the player widget takes as its `feed` - so
 * a library track needs nothing else to play.
 *
 * `length` is milliseconds, as everywhere else in Fadila; Mixcloud's
 * `audio_length` is seconds. A show still being processed has none, and
 * UNAVAILABLE is the right answer for it rather than a zero that would read
 * as a real duration.
 *
 * Two extra own properties beyond Track's, both plain data, so they survive
 * the JSON round trip through the library file as `missing` does; nothing
 * here is an instance method (see ARCHITECTURE.md section 2).
 */
export default class MixcloudTrack extends Track {
  static from(cloudcast: MixcloudCloudcast) {
    return new MixcloudTrack(cloudcast);
  }

  /** The show's page on mixcloud.com, for the "Open on Mixcloud" item. */
  url: string;

  /** Artwork, for a future now-playing view. Empty when the show has none. */
  artworkUrl: string;

  constructor(cloudcast: MixcloudCloudcast) {
    super(
      cloudcast.key, // we throw on null right after
      Source.Mixcloud,
      decodeHtml(cloudcast.name || ''),
      decodeHtml(artistOf(cloudcast)),
      '',
      cloudcast.audio_length ? cloudcast.audio_length * 1000 : TrackLength.UNAVAILABLE,
      cloudcast.tags && cloudcast.tags.length ? cloudcast.tags[0].name : '',
      cloudcast.created_time ? Date.parse(cloudcast.created_time) || 0 : 0,
    );

    if (!cloudcast.key) throw new Error('Missing key for MixcloudTrack');

    // The account, always - which is the artist too on a show with no named
    // hosts, and the label or station behind the DJ on one that has them.
    this.uploader = cloudcast.user ? decodeHtml(cloudcast.user.name) : '';

    this.url = cloudcast.url;
    this.artworkUrl = (cloudcast.pictures && cloudcast.pictures.large) || '';
  }
}
